import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Loader2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const CambiaTipoDialog = ({ pratica, onClose }) => {
  const [tipoId, setTipoId] = useState("");
  const [sostituisci, setSostituisci] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: tipi } = useQuery({
    queryKey: ["tipi-pratica"],
    queryFn: async () => (await api.get("/tipi-pratica")).data,
  });
  const disponibili = (tipi || []).filter((t) => t.id !== pratica.tipo_pratica_id);
  const nuovo = disponibili.find((t) => t.id === tipoId);
  const fatti = (pratica.steps || []).filter((s) => s.completato).map((s) => s.nome);
  const mantenuti = nuovo ? (nuovo.steps || []).filter((s) => fatti.includes(s.nome)).length : 0;

  const salva = async () => {
    if (!tipoId) { toast.error("Scegli il nuovo tipo"); return; }
    setBusy(true);
    try {
      await api.put(`/pratiche/${pratica.id}/tipo`, { tipo_pratica_id: tipoId, sostituisci_step: sostituisci });
      toast.success(`Tipo cambiato in «${nuovo.nome}»`);
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cambio tipo non riuscito"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A2B4A]/45 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="cambia-tipo-dialog">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#1A2B4A]/10">
          <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#1A2B4A] flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-[#C9A84C]" /> Cambia tipo pratica
          </h3>
          <button onClick={() => onClose(false)} data-testid="cambia-tipo-chiudi" className="p-1 rounded-md hover:bg-[#f9f8f5]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-[12px] text-[#4a5568]">
            Tipo attuale: <b>{pratica.tipo_pratica_nome}</b> · {fatti.length} step completati.
          </p>

          <div>
            <label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#8a93a6]">Nuovo tipo</label>
            <select data-testid="cambia-tipo-select" value={tipoId} onChange={(e) => setTipoId(e.target.value)}
              className="mt-1 w-full bg-white border border-[#1A2B4A]/15 rounded-md px-3 py-2 text-sm">
              <option value="">— scegli —</option>
              {disponibili.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#8a93a6]">Step della pratica</label>
            <label className="flex gap-2 items-start cursor-pointer border border-[#1A2B4A]/12 rounded-md px-3 py-2 has-[:checked]:border-[#C9A84C] has-[:checked]:bg-[#fdfaf1]">
              <input type="radio" name="step-mode" data-testid="cambia-tipo-tieni" checked={!sostituisci}
                onChange={() => setSostituisci(false)} className="mt-0.5" />
              <span className="text-[12px] text-[#1A2B4A]">
                <b>Tieni gli step attuali</b> (consigliato) — la pratica conserva tutte le spunte già fatte.
              </span>
            </label>
            <label className="flex gap-2 items-start cursor-pointer border border-[#1A2B4A]/12 rounded-md px-3 py-2 has-[:checked]:border-[#C9A84C] has-[:checked]:bg-[#fdfaf1]">
              <input type="radio" name="step-mode" data-testid="cambia-tipo-sostituisci" checked={sostituisci}
                onChange={() => setSostituisci(true)} className="mt-0.5" />
              <span className="text-[12px] text-[#1A2B4A]">
                <b>Sostituisci con gli step del nuovo tipo</b>
                {nuovo && <> — {nuovo.steps?.length} step; restano spuntati i {mantenuti} con lo stesso nome, gli altri tornano da fare.</>}
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#1A2B4A]/10">
          <button onClick={() => onClose(false)} className="px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-md hover:bg-[#f9f8f5]">
            Annulla
          </button>
          <button data-testid="cambia-tipo-salva" onClick={salva} disabled={busy || !tipoId}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-md font-semibold hover:bg-[#22345c] disabled:opacity-40">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Cambia tipo
          </button>
        </div>
      </div>
    </div>
  );
};

export default CambiaTipoDialog;
